const PLACEHOLDER_SRC = '/assets/images/placeholder.svg';
const ALL_CATEGORY_ID = 'all';
const MISC_CATEGORY_ID = 'misc';
const MIN_VISIBLE_CATEGORY_COUNT = 4;

let detachKeydown = null;

const normalize = (value) => String(value || '').toLowerCase().trim();

const ensureModal = () => {
  let modal = document.getElementById('prompt-image-picker-modal');
  if (modal) return modal;

  modal = document.createElement('div');
  modal.id = 'prompt-image-picker-modal';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');

  const panel = document.createElement('div');
  panel.className = 'picker-panel';
  modal.appendChild(panel);
  document.body.appendChild(modal);
  return modal;
};

const openModal = (modal) => {
  modal.classList.add('show');
};

const closeModal = (modal) => {
  modal.classList.remove('show');
};

export const openPromptImagePicker = ({
  title,
  items,
  categories = [],
  searchPlaceholder = 'Search...',
  showSearch = true,
  selectedId = null,
  onSelect,
} = {}) => {
  const modal = ensureModal();
  const panel = modal.querySelector('.picker-panel');
  if (!panel) return;
  detachKeydown?.();

  const catalog = Array.isArray(items) ? items : [];
  const categoryList = Array.isArray(categories) ? categories : [];
  const categoryCount = (categoryId) => catalog.filter((item) => (
    Array.isArray(item.categories) && item.categories.includes(categoryId)
  )).length;
  const keptCategories = categoryList.filter((category) => categoryCount(category.id) >= MIN_VISIBLE_CATEGORY_COUNT);
  const foldedCategoryIds = new Set(
    categoryList
      .filter((category) => {
        const count = categoryCount(category.id);
        return count > 0 && count < MIN_VISIBLE_CATEGORY_COUNT;
      })
      .map((category) => category.id),
  );
  const miscHasItems = catalog.some((item) => (
    Array.isArray(item.categories) && item.categories.some((categoryId) => foldedCategoryIds.has(categoryId))
  ));
  const filterCategories = keptCategories.length && miscHasItems
    ? [...keptCategories, { id: MISC_CATEGORY_ID, label: 'Misc' }]
    : keptCategories;
  let searchTerm = '';
  let activeCategory = ALL_CATEGORY_ID;
  let isClosing = false;

  const handleClose = () => {
    if (isClosing) return;
    isClosing = true;
    closeModal(modal);
    detachKeydown?.();
    detachKeydown = null;
  };

  const onKeyDownCapture = (event) => {
    const key = event.key || event.code;
    if (key !== 'Escape' && key !== 'Esc') return;
    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
    handleClose();
  };

  const render = () => {
    panel.innerHTML = '';
    modal.setAttribute('aria-label', title || 'Select an image');

    const header = document.createElement('div');
    header.className = 'picker-header';

    const titleEl = document.createElement('div');
    titleEl.className = 'picker-title';
    titleEl.textContent = title || 'Select';

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'picker-close';
    closeBtn.textContent = '×';
    closeBtn.tabIndex = 0;
    closeBtn.setAttribute('aria-label', 'Close');
    closeBtn.addEventListener('click', handleClose);

    header.appendChild(titleEl);
    header.appendChild(closeBtn);

    let searchInput = null;
    let searchWrap = null;
    if (showSearch) {
      searchWrap = document.createElement('div');
      searchWrap.className = 'picker-search';

      searchInput = document.createElement('input');
      searchInput.type = 'text';
      searchInput.value = searchTerm;
      searchInput.placeholder = searchPlaceholder;
      searchInput.setAttribute('aria-label', searchPlaceholder);
      searchInput.addEventListener('input', (event) => {
        searchTerm = event?.target?.value || '';
        render();
      });
      searchWrap.appendChild(searchInput);
    }

    const query = showSearch ? normalize(searchTerm) : '';
    const filtered = catalog.filter((item) => {
      const itemCategories = Array.isArray(item.categories) ? item.categories : [];
      if (activeCategory === MISC_CATEGORY_ID) {
        if (!itemCategories.some((categoryId) => foldedCategoryIds.has(categoryId))) return false;
      } else if (activeCategory !== ALL_CATEGORY_ID) {
        if (!itemCategories.includes(activeCategory)) return false;
      }
      if (!query) return true;
      return normalize(item.label).includes(query);
    });

    const listWrap = document.createElement('div');
    listWrap.className = 'picker-list';

    const grid = document.createElement('div');
    grid.className = 'picker-grid';

    if (!filtered.length) {
      const empty = document.createElement('p');
      empty.className = 'picker-empty';
      empty.textContent = 'No matches';
      grid.appendChild(empty);
    }

    filtered.forEach((item) => {
      const label = String(item.label || '').trim();
      if (!label) return;

      const card = document.createElement('button');
      card.type = 'button';
      card.className = 'prompt-image-card';
      card.tabIndex = 0;
      card.setAttribute('aria-label', `Select ${label}`);
      const isSelected = selectedId != null && String(item.id) === String(selectedId);
      card.classList.toggle('is-selected', isSelected);
      card.setAttribute('aria-pressed', isSelected ? 'true' : 'false');

      const image = document.createElement('img');
      image.alt = label;
      image.loading = 'lazy';
      image.src = item.imageUrl || PLACEHOLDER_SRC;
      image.addEventListener('error', () => {
        if (image.dataset.fallback === '1') return;
        image.dataset.fallback = '1';
        image.src = PLACEHOLDER_SRC;
      });

      const name = document.createElement('span');
      name.className = 'prompt-image-card__label';
      name.textContent = label;

      card.appendChild(image);
      card.appendChild(name);

      const handlePick = () => {
        if (typeof onSelect === 'function') onSelect(item);
        handleClose();
      };
      card.addEventListener('click', handlePick);
      card.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        handlePick();
      });
      grid.appendChild(card);
    });

    listWrap.appendChild(grid);
    panel.appendChild(header);
    if (searchWrap) panel.appendChild(searchWrap);

    if (filterCategories.length) {
      const filters = document.createElement('div');
      filters.className = 'picker-filters';
      filters.setAttribute('role', 'tablist');
      filters.setAttribute('aria-label', 'Categories');

      [{ id: ALL_CATEGORY_ID, label: 'All' }, ...filterCategories].forEach((category) => {
        const filterBtn = document.createElement('button');
        filterBtn.type = 'button';
        filterBtn.className = 'picker-filter';
        filterBtn.tabIndex = 0;
        filterBtn.textContent = category.label;
        filterBtn.setAttribute('aria-label', category.label);
        const isActive = category.id === activeCategory;
        filterBtn.classList.toggle('is-active', isActive);
        filterBtn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        const handleFilter = () => {
          activeCategory = category.id;
          render();
        };
        filterBtn.addEventListener('click', handleFilter);
        filterBtn.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          handleFilter();
        });
        filters.appendChild(filterBtn);
      });
      panel.appendChild(filters);
    }

    panel.appendChild(listWrap);

    requestAnimationFrame(() => {
      if (searchInput) {
        searchInput.focus();
        const cursor = searchInput.value.length;
        searchInput.setSelectionRange(cursor, cursor);
        return;
      }
      const selectedCard = grid.querySelector('.prompt-image-card.is-selected');
      const focusTarget = selectedCard || grid.querySelector('.prompt-image-card');
      if (focusTarget instanceof HTMLElement) focusTarget.focus();
    });
  };

  modal.onclick = (event) => {
    if (event.target === modal) handleClose();
  };

  document.addEventListener('keydown', onKeyDownCapture, true);
  detachKeydown = () => document.removeEventListener('keydown', onKeyDownCapture, true);
  render();
  openModal(modal);
};
